/**
 * Arista Switch Database
 *
 * Arista switches for AI/ML GPU clusters.
 *
 * Data sources: research/switch-specs.md
 */

import type { SwitchModel } from '../../types/switches';

/**
 * Arista 7060X6-64PE (800G, 64-port)
 */
export const ARISTA_7060X6_64PE: SwitchModel = {
  id: 'arista-7060x6-64pe',
  name: '7060X6-64PE',
  vendor: 'Arista',
  productName: 'Arista 7060X6-64PE',
  fabricType: 'Ethernet',
  typicalRoles: ['leaf', 'spine'],
  ports: {
    totalPorts: 64,
    speed: {
      value: 800,
      unit: 'Gbps',
      displayName: '800GbE',
    },
    portType: 'OSFP',
    supportsBreakout: true,
    breakoutOptions: ['64x800G', '128x400G', '256x200G', 'Up to 320 interfaces mixed'],
  },
  power: {
    typicalWatts: 1000,
    maxWatts: 1700,
    idleWatts: 420,
    perPortWatts: 12,
    psuConfig: {
      count: 2,
      capacity: 2000,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 2,
    widthMm: 438,
    depthMm: 600,
    weightKg: 22,
    airflow: 'front-to-back',
    maxBtuPerHour: 5800,
  },
  performance: {
    switchingCapacityTbps: 51.2,
    forwardingRateMpps: 38400,
    latencyNs: 320,
    bufferSizeMB: 128,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: true,
    evpn: true,
    rdma: true,
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    additionalFeatures: [
      'Single-chip platform',
      'Highest density 800G switching',
      'Multi-speed support (10/25/50/100/200/400/800GbE)',
      'Wide optics/cable support',
      'Arista EOS',
    ],
  },
  metadata: {
    productUrl: 'https://www.arista.com/assets/data/pdf/Datasheets/7060X6-Datasheet.pdf',
    releaseYear: 2023,
    endOfLife: false,
    notes: 'Single-chip 800G platform optimized for AI leaf and spine clusters.',
  },
};

/**
 * Arista 7060X6-32PE (800G, 32-port)
 */
export const ARISTA_7060X6_32PE: SwitchModel = {
  id: 'arista-7060x6-32pe',
  name: '7060X6-32PE',
  vendor: 'Arista',
  productName: 'Arista 7060X6-32PE',
  fabricType: 'Ethernet',
  typicalRoles: ['leaf'],
  ports: {
    totalPorts: 32,
    speed: {
      value: 800,
      unit: 'Gbps',
      displayName: '800GbE',
    },
    portType: 'OSFP',
    supportsBreakout: true,
    breakoutOptions: ['32x800G', '64x400G', '128x200G', 'Mixed speeds'],
  },
  power: {
    typicalWatts: 550,
    maxWatts: 950,
    idleWatts: 250,
    perPortWatts: 12,
    psuConfig: {
      count: 2,
      capacity: 1200,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 1,
    widthMm: 438,
    depthMm: 550,
    weightKg: 15,
    airflow: 'front-to-back',
    maxBtuPerHour: 3240,
  },
  performance: {
    switchingCapacityTbps: 25.6,
    forwardingRateMpps: 19200,
    latencyNs: 320,
    bufferSizeMB: 64,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: true,
    evpn: true,
    rdma: true,
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    additionalFeatures: [
      'Compact 1RU design',
      'Single-chip platform',
      'Multi-speed support',
      'Arista EOS',
    ],
  },
  metadata: {
    productUrl: 'https://www.arista.com/assets/data/pdf/Datasheets/7060X6-Datasheet.pdf',
    releaseYear: 2023,
    endOfLife: false,
    notes: 'Compact 1U 32-port 800G switch for smaller AI deployments.',
  },
};

/**
 * Arista 7800R3 Series (Modular Chassis)
 * Using 7816R as representative model
 */
export const ARISTA_7816R: SwitchModel = {
  id: 'arista-7816r',
  name: '7816R',
  vendor: 'Arista',
  productName: 'Arista 7816R',
  fabricType: 'Ethernet',
  typicalRoles: ['spine', 'super-spine'],
  ports: {
    totalPorts: 576, // Max with 16 line cards (36 ports each)
    speed: {
      value: 400,
      unit: 'Gbps',
      displayName: '400GbE',
    },
    portType: 'QSFP56-DD',
    supportsBreakout: true,
    breakoutOptions: ['576x400G', '768x100G', 'Mixed configurations'],
  },
  power: {
    typicalWatts: 8000, // Chassis power estimate
    maxWatts: 16000,
    idleWatts: 3000,
    perPortWatts: 15,
    psuConfig: {
      count: 4,
      capacity: 5000,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 32,
    widthMm: 438,
    depthMm: 800,
    weightKg: 150,
    airflow: 'front-to-back',
    maxBtuPerHour: 54600,
  },
  performance: {
    switchingCapacityTbps: 460,
    forwardingRateMpps: 345000,
    latencyNs: 400,
    bufferSizeMB: 2048, // Deep buffer VOQ architecture
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: true,
    evpn: true,
    rdma: true,
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    additionalFeatures: [
      'Virtual Output Queue (VOQ) architecture',
      'Eliminates head-of-line blocking',
      'Deep buffer design',
      'Modular line cards',
      'HVAC/HVDC/LVDC power options',
      'Arista EOS',
    ],
  },
  metadata: {
    productUrl: 'https://www.arista.com/assets/data/pdf/Datasheets/7800R3-Data-Sheet.pdf',
    releaseYear: 2022,
    endOfLife: false,
    notes: 'Modular chassis for large multi-tier GPU networks. 16-slot line card capacity. Can pair with 7060X6 series.',
  },
};

/**
 * All Arista switches
 */
export const ARISTA_SWITCHES: readonly SwitchModel[] = [
  ARISTA_7060X6_64PE,
  ARISTA_7060X6_32PE,
  ARISTA_7816R,
] as const;

/**
 * Get Arista switch by ID
 */
export function getAristaSwitch(id: string): SwitchModel | undefined {
  return ARISTA_SWITCHES.find((sw) => sw.id === id);
}
